import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Per-tier feature bullets, matching the 5-tier pricing sheet. Keyed by the plan's canonical
// `name` (Free/Monitoring/Pro/Intelligence/Enterprise) rather than derived from AppFeature flags,
// since several sheet rows (e.g. small-value procurements being a full vs. teaser experience)
// aren't distinguishable from the feature list alone. comingSoon items are sheet rows not yet
// built - grouped under their own heading at the end of the list rather than omitted, per
// product decision: the difference between shipped and planned has to stay visible.
export const TIER_FEATURE_BULLETS = {
    Monitoring: [
        { key: 'everythingInFree', defaultText: 'Everything in Free, plus:', heading: true },
        { key: 'procurementMonitoring', defaultText: 'Procurement monitoring: email alerts on new matches' },
        { key: 'smallscaleFull', defaultText: 'Full access to small-value procurements' },
        { key: 'noticeAiChat', defaultText: 'Notice AI chat: ask about any tender and its attachments' },
        { key: 'kanban', defaultText: 'Visual Kanban pipeline for your bids' },
        { key: 'teamTracking', defaultText: 'Organisation & team bid tracking' },
        { key: 'versionComparison', defaultText: 'Tender version comparison' },
        { key: 'excelExport', defaultText: 'PDF → Excel criteria extraction' },
        { key: 'aiAttachmentSummary', defaultText: 'Comprehensive AI summary of tender attachments', comingSoon: true },
    ],
    Pro: [
        { key: 'everythingInMonitoring', defaultText: 'Everything in Monitoring, plus:', heading: true },
        { key: 'boardMinutes', defaultText: 'Municipal, wellbeing-region & county board minutes' },
        { key: 'officialDecisions', defaultText: 'Official-holder decisions' },
        // Kilpailija-analyysi avattiin Prolle 9.10.2026; rivi siirrettiin Intelligencestä myyjän korjauksella 23.10.
        { key: 'competitorTracking', defaultText: 'Competitor tracking in board minutes' },
        { key: 'investmentPlans', defaultText: 'Investment plans & upcoming projects' },
        { key: 'marketDataSummary', defaultText: 'Basic AI summary of market-data results' },
        { key: 'emailAlerts', defaultText: 'Email alerts on new market-data matches' },
        { key: 'aiAssistant', defaultText: 'AI search assistant' },
    ],
    Intelligence: [
        { key: 'everythingInPro', defaultText: 'Everything in Pro, plus:', heading: true },
        // Valmiit ominaisuudet ensin (Petrin lista 2.9.2026), tulossa-osio perässä.
        { key: 'deeperSummary', defaultText: 'Deeper AI summaries with follow-up chat' },
        { key: 'similarProjects', defaultText: 'AI comparison of similar past procurements from board minutes' },
        { key: 'pdfReports', defaultText: 'Downloadable PDF reports' },
        { key: 'projectDeepDive', defaultText: 'AI deep-dive analysis of past projects' },
        { key: 'crmIntegration', defaultText: 'Push new hits to your CRM (HubSpot live, more coming)' },
        { key: 'newsletters', defaultText: 'Digest: automated & customisable newsletters, weekly or on weekdays' },
        { key: 'mcp', defaultText: 'Tendera in your own AI (MCP): ChatGPT, Claude, Gemini' },
        { key: 'suitabilityAssessment', defaultText: 'AI-based suitability assessment', comingSoon: true },
        { key: 'companyProfile', defaultText: 'Company profile (certificates, references, products, deal-value preferences)', comingSoon: true },
        { key: 'customQuestions', defaultText: 'Customisable AI summary questions', comingSoon: true },
        { key: 'deepSearch', defaultText: 'Deep search, company-aware answers', comingSoon: true },
        { key: 'priceBenchmarking', defaultText: 'Comparison-table price benchmarking', comingSoon: true },
        { key: 'priceTrends', defaultText: 'Historical price-trend analytics', comingSoon: true },
        { key: 'multiFocus', defaultText: 'Multi-focus AI summaries (sales, decisions, competitors, overview)', comingSoon: true },
    ],
    Enterprise: [
        { key: 'everythingInIntelligence', defaultText: 'Everything in Intelligence, plus:', heading: true },
        { key: 'apiIntegration', defaultText: 'API integration', comingSoon: true },
        { key: 'negotiatedTerms', defaultText: 'Negotiated seats & terms tailored to your organisation' },
    ],
};

/**
 * Feature bullets of one paid tier. Shared by the Billing page and the plan dialog so the two can't
 * drift apart (until 29.9.2026 the dialog showed one generic list for every paid plan).
 */
export function TierFeatureList({ planName }) {
    const { t } = useTranslation();
    const bullets = TIER_FEATURE_BULLETS[planName];
    if (!bullets) return null;
    const label = (bullet) => t(`pages.settings.billing.tierFeatures.${planName.toLowerCase()}.${bullet.key}`, bullet.defaultText);
    return (
        <>
            {/* Käytettävissä olevat ensin, tulossa olevat omana ryhmänään lopussa. Yksi
                otsikko korvaa kymmenkunta toistuvaa merkintää, jolloin kortti lyhenee ja
                ero valmiin ja tulevan välillä näkyy yhdellä silmäyksellä. */}
            {bullets.filter((bullet) => !bullet.comingSoon).map((bullet) => (
                bullet.heading ? (
                    <div className="feature feature-heading" key={bullet.key}>
                        {label(bullet)}
                    </div>
                ) : (
                    <div className="feature" key={bullet.key}>
                        <CheckOutlined style={{ color: '#52c41a' }} />
                        {' '}
                        {label(bullet)}
                    </div>
                )
            ))}
            {bullets.some((bullet) => bullet.comingSoon) && (
                <div className="feature-coming-soon">
                    <div className="feature-coming-soon__heading">
                        {t('pages.settings.billing.comingSoonHeading', 'Tulossa pian:')}
                    </div>
                    {bullets.filter((bullet) => bullet.comingSoon).map((bullet) => (
                        <div className="feature" key={bullet.key}>
                            {label(bullet)}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
